import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { resolveApiUrl } from '../../../environments/resolve-api-url';

export interface UploadResponse {
  url?: string;
  urls?: string[];
}

export interface UploadResult {
  success: boolean;
  url?: string;
  urls?: string[];
  error?: string;
}

export interface UserProfileRole {
  roleId: number;
  roleName: string;
}

export interface UserProfileResponse {
  accountNo: number;
  password: string;
  roles: UserProfileRole[];
  userDescription: string;
  userEmergencyPassword: string;
  userEndEmergencyRoleDate: string;
  userFirstName: string;
  userId: number;
  userIdleTime: number;
  userIsActive: boolean;
  userLastName: string;
  userName: string;
  userStartEmergencyRoleDate: string;
  userTypeBiller: boolean;
  userTypeDoctor: boolean;
  userTypeEmergencyDoctor: boolean;
  userTypeEmergencyFrontDesk: boolean;
  userTypeEmergencyNurse: boolean;
  userTypeFrontDesk: boolean;
  userTypeNurse: boolean;
  userTypePatient: boolean;
  userWorkFromTime: string;
  userWorkToTime: string;
}

interface ProfileApiEnvelope {
  status?: string;
  message?: string;
  data?: UserProfileResponse;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private apiUrl = resolveApiUrl();

  constructor(private http: HttpClient) {}

  /** GET /api/v1/profile */
  getProfile(): Observable<UserProfileResponse> {
    return this.http.get<ProfileApiEnvelope | UserProfileResponse>(`${this.apiUrl}/profile`).pipe(
      map((response: ProfileApiEnvelope | UserProfileResponse) => {
        const wrapped = response as ProfileApiEnvelope;
        if (wrapped && wrapped.data && typeof wrapped.data === 'object') {
          return wrapped.data;
        }
        return response as UserProfileResponse;
      })
    );
  }

  /**
   * Upload a single file to Azure Blob Storage
   * POST /api/v1/profile/upload
   * 
   * @param file - The file to upload
   * @returns Observable<UploadResult>
   */
  uploadFile(file: File): Observable<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<any>(`${this.apiUrl}/profile/upload`, formData).pipe(
      map((response: any) => {
        // Handle nested structure: response.data
        if (response && response.data) {
          return {
            success: true,
            url: response.data.url || undefined,
            urls: response.data.urls || undefined
          } as UploadResult;
        }
        
        // Handle direct response structure
        if (response && (response.url || response.urls)) {
          return {
            success: true,
            url: response.url || undefined,
            urls: response.urls || undefined
          } as UploadResult;
        }
        
        return {
          success: false,
          error: 'Invalid response format'
        };
      })
    );
  }

  /**
   * Upload multiple files to Azure Blob Storage
   * POST /api/v1/profile/upload
   * 
   * @param files - Array of files to upload
   * @returns Observable<UploadResult>
   */
  uploadFiles(files: File[]): Observable<UploadResult> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file);
    });

    return this.http.post<any>(`${this.apiUrl}/profile/upload`, formData).pipe(
      map((response: any) => {
        // Handle nested structure: response.data
        if (response && response.data) {
          return {
            success: true,
            url: response.data.url || undefined,
            urls: response.data.urls || undefined
          } as UploadResult;
        }
        
        // Handle direct response structure
        if (response && (response.url || response.urls)) {
          return {
            success: true,
            url: response.url || undefined,
            urls: response.urls || undefined
          } as UploadResult;
        }
        
        return {
          success: false,
          error: 'Invalid response format'
        };
      })
    );
  }

  /**
   * Upload patient photo
   * Convenience method for uploading a single patient photo
   * 
   * @param photo - The photo file to upload
   * @returns Observable<string> - Returns the URL of the uploaded photo
   */
  uploadPatientPhoto(photo: File): Observable<string> {
    return this.uploadFile(photo).pipe(
      map((result: UploadResult) => {
        if (result.success && result.url) {
          return result.url;
        }
        if (result.success && result.urls && result.urls.length > 0) {
          return result.urls[0];
        }
        throw new Error(result.error || 'Failed to upload photo');
      })
    );
  }
}
